import { Button, Divider, Grid, Paper, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { deleteProfileGroup, getProfileGroupById, updateProfileGroup } from 'src/shared/services/profileGroupService';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { toast } from 'react-toastify';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { ProfileGroup } from 'src/modules/member/models/profileGroup';
import { ProfileGroupRequest } from '../models/profileGroupRequest';
import { ProfileGroupSection } from '../components/profileGroupSection';
import { ProfileItemSection } from '../components/profileItemSection';

export const MemberProfileDetailPage = () => {
  const { t } = useTranslation(['fundraiser']);
  const params = useParams();
  const isMounted = useIsMounted();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [dataLoaded, setDataLoaded] = useState(true);
  const [profileGroup, setProfileGroup] = useState<ProfileGroup>();

  const requiredMessage = t('common:validationMessages.required');
  const maxChar = 50;

  const profileItemSchema = yup.object().shape({
    memberProfileItem_id: yup.number().nullable(),
    memberProfile_id: yup.number().required(),
    name: yup
      .string()
      .max(maxChar, t('common:validationMessages.maxLength', { count: maxChar }))
      .required(requiredMessage),
    pos: yup.number().required(),
    groupName: yup.string().optional().nullable(),
    text: yup.string().optional().nullable(),
  });

  const profileGroupSchema: Schema<ProfileGroupRequest> = yup.object().shape({
    memberProfile_id: yup.number().required(requiredMessage),
    title: yup
      .string()
      .max(maxChar, t('common:validationMessages.maxLength', { count: maxChar }))
      .required(requiredMessage),
    countyAss_id: yup.number().required(requiredMessage),
    localAss_id: yup.number().required(requiredMessage),
    userFlags: yup.number().min(1, t('memberProfile:profileDetail.profileAvailableError')).required(requiredMessage),
    type: yup.number().required(requiredMessage),
    profileItems: yup
      .array()
      .of(profileItemSchema)
      .min(1, t('memberProfile:profileDetail.atLeastOneProfile'))
      .test('unique-names', t('memberProfile:profileDetail.duplicateNameError'), (items) => {
        if (!items) return true;
        const names = items.map((item) => item.name?.trim().toLowerCase());
        const uniqueNames = new Set(names);
        return uniqueNames.size === names.length;
      }),
  });

  const id = Number(params.id);
  const isValidId = Number.isInteger(id) && id > 0;

  const formMethods = useForm<ProfileGroupRequest>({
    resolver: yupResolver(profileGroupSchema),
    mode: 'all',
    defaultValues: {
      memberProfile_id: Number(params.id) || 0,
      title: '',
      countyAss_id: 0,
      localAss_id: 0,
      userFlags: 0,
      type: 0,
      profileItems: [],
      deleteProfileItemsIds: [],
    },
  });

  const {
    handleSubmit,
    watch,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = formMethods;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getProfileGroupById(params.id);
      if (isMounted()) {
        const sortedData = {
          ...result.data,
          minSelectionCount: result.data.minSelectionCount ?? 0,
          maxSelectionCount: result.data.maxSelectionCount ?? 0,
          errorMessage: result.data.errorMessage ?? '',
          profileItems: result.data.profileItems?.slice().sort((a, b) => a.pos - b.pos) || [],
        };
        setProfileGroup(sortedData);
        reset(sortedData);
      }
    } catch (e) {
      ConsoleHelper.log(e);
      setDataLoaded(false);
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [isMounted, params.id, reset]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  const onSubmit = async (data: ProfileGroupRequest) => {
    setLoading(true);

    const dataToPost: ProfileGroupRequest = {
      ...data,
      minSelectionCount: Number(data.minSelectionCount),
      maxSelectionCount: Number(data.maxSelectionCount),
    };
    try {
      await updateProfileGroup(dataToPost);
      if (isMounted()) {
        toast.success(<>{t('memberProfile:profileDetail.putSuccess')}</>);
        fetchData();
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.error(<>{t('memberProfile:profileDetail.putError')}</>);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  const redirect = () => {
    navigate(`${RoutesEnum.MemberProfileGroup}`);
  };

  const handleDeleteProfileGroup = async () => {
    setLoading(true);
    try {
      await deleteProfileGroup(Number(params.id));
      if (isMounted()) {
        toast.success(<>{t('memberProfile:profileDetail.deleteSuccess')}</>);
        redirect();
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.success(<>{t('memberProfile:profileDetail.deleteError')}</>);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  const syncPositions = () => {
    const items = getValues('profileItems');
    if (!items) return;

    items.forEach((_, index) => {
      setValue(`profileItems.${index}.pos`, index + 1);
    });
  };

  return (
    <>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormLoaderWrapper loading={loading}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <MainContentHeader title={watch('title') ?? ''} />
              </Grid>
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  {dataLoaded ? (
                    <>
                      <Grid spacing={3} container justifyContent='space-between'>
                        <Grid item sm={12} lg={7}>
                          <ProfileGroupSection isEditable={profileGroup?.isEditable ?? false} loading={loading} />
                        </Grid>

                        <Grid item xs='auto' sx={{ display: { xs: 'none', lg: 'block' } }}>
                          <Divider orientation='vertical' sx={{ widht: '1px' }} />
                        </Grid>
                        <Grid item xs={12} sx={{ display: { xs: 'block', lg: 'none' }, my: 2 }}>
                          <Divider orientation='horizontal' sx={{ height: '1px', width: '100%' }} />
                        </Grid>

                        <Grid item xs={12} lg={4.5}>
                          {isValidId && (
                            <>
                              <ProfileItemSection
                                isEditable={profileGroup?.isEditable ?? false}
                                memberProfileId={id}
                                syncPositions={() => syncPositions()}
                                loading={loading}
                              />
                              {errors.profileItems?.message && (
                                <Typography color='error' sx={{ mt: 1 }}>
                                  {errors.profileItems.message}
                                </Typography>
                              )}
                            </>
                          )}
                        </Grid>
                      </Grid>
                      <Grid item xs={12} mt={4} display='flex' justifyContent='flex-end' gap={2} alignItems='center'>
                        <Button
                          size='medium'
                          variant='outlined'
                          color='error'
                          onClick={() => setOpenDeleteDialog(true)}
                          disabled={loading || !profileGroup?.isEditable}
                        >
                          {t('common:button.delete')}
                        </Button>
                        <Button size='medium' variant='contained' color='primary' type='submit' disabled={loading || !profileGroup?.isEditable}>
                          {t('common:button.save')}
                        </Button>
                      </Grid>
                    </>
                  ) : (
                    <Typography variant='h5' sx={{ fontWeight: 500, color: 'text.secondary' }}>
                      {t('memberProfile:profileDetail.profileNotFound')}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </FormLoaderWrapper>
        </form>
      </FormProvider>
      {openDeleteDialog && (
        <DeleteConfirmationDialog
          title={t('common:confirmDelete')}
          modaltext={
            profileGroup?.profileGroupMemberCount === 1
              ? t('memberProfile:profileDetail.profileGroupWarning', { count: profileGroup?.profileGroupMemberCount })
              : t('memberProfile:profileDetail.profileGroupWarningPlural', { count: profileGroup?.profileGroupMemberCount })
          }
          value=''
          open={openDeleteDialog}
          onClose={(val) => {
            if (val) {
              handleDeleteProfileGroup();
            } else {
              setOpenDeleteDialog(false);
            }
          }}
        />
      )}
    </>
  );
};
